<template>
  <b-card no-body>
    <template v-slot:header>
      <h2 class="mb-0">
        <template v-if="innerInvoice.id">Редактирование коммерческого предложения # {{ innerInvoice.id }}</template>
        <template v-else>Новое коммерческое предложение</template>
      </h2>
    </template>

    <div class="block" v-if="Object.keys($page.props.errors).length > 0">
      <b-alert class="mb-0" variant="danger" :show="Object.keys($page.props.errors).length > 0">
        <h4 class="alert-heading">Исправьте ошибки и попробуйте сохранить снова</h4>
        <ul class="list-unstyled mb-0">
          <template v-for="(errors, name) in $page.props.errors">
            <li v-for="(error, i) in errors" :key="name+i">
              {{ error }}
            </li>
          </template>
        </ul>
      </b-alert>
    </div>

    <b-form @submit.prevent="onSubmit">
      <div class="block bg-galsen">
        <h3>Статус и документы</h3>
        <b-form-row>
          <div class="col">
            <b-form-group label="Статус">
              <b-form-select v-model="innerInvoice.status">
                <option value="1">Новое</option>
                <option value="2">Закрыто</option>
              </b-form-select>
            </b-form-group>
          </div>
          <div class="col">
            <b-form-group label="Ссылка на папку с документами" :invalid-feedback="fieldError('docs_dir_link')">
              <b-input v-model="innerInvoice.docs_dir_link"
                       :state="fieldState('company.docs_dir_link')"></b-input>
            </b-form-group>
          </div>
        </b-form-row>

      </div>

      <div class="block">
        <h3>Компания и контакт</h3>
        <b-form-row>
          <div class="col-9">
            <b-form-group label="Компания" :invalid-feedback="fieldError('company.name')"
                          :state="fieldState('company.name')">
              <suggestions v-model="innerInvoice.company.name"
                           :options="{inputClass: 'form-control autocomplete', placeholder: 'Поиск по названию компании'}"
                           :onItemSelected="onCompanySelected"
                           :onInputChange="onCompanySearch" ref="companyNameEl">
                <div slot="item" slot-scope="props" class="single-item">
                  <div class="name"><b>{{ props.item.name }}</b></div>
                  <div class="name">{{ props.item.city }}</div>
                </div>
              </suggestions>
            </b-form-group>
          </div>
          <div class="col">
            <b-form-group label="Город" :invalid-feedback="fieldError('company.city')">
              <b-input v-model="innerInvoice.company.city" :disabled="isCompanyEditingDisabled('city')"
                       :state="fieldState('company.city')"></b-input>
            </b-form-group>
          </div>
        </b-form-row>

        <b-form-row>
          <div class="col">
            <b-form-group label="Ф. И. О." :invalid-feedback="fieldError('person.name')" :state="fieldState('person.name')">
              <b-input v-model="innerInvoice.person.name" :state="fieldState('person.name')"></b-input>
            </b-form-group>
          </div>
          <div class="col">
            <b-form-group label="Должность" :invalid-feedback="fieldError('person.position')">
              <b-input v-model="innerInvoice.person.position" :state="fieldState('person.position')"></b-input>
            </b-form-group>
          </div>
          <div class="col">
            <b-form-group label="Телефон" :invalid-feedback="fieldError('person.phone')">
              <b-input v-model="innerInvoice.person.phone" :state="fieldState('person.phone')"></b-input>
            </b-form-group>
          </div>
          <div class="col">
            <b-form-group label="Email" :invalid-feedback="fieldError('person.email')">
              <b-input v-model="innerInvoice.person.email" :state="fieldState('person.email')"></b-input>
            </b-form-group>
          </div>
        </b-form-row>
      </div>

      <div class="block bg-galsen">
        <h3>Состав</h3>
        <b-form-row>
          <div class="col-4">
            <b-form-group label="Код">
              <suggestions v-model="setQuery"
                           :options="{ inputClass: 'form-control autocomplete',
                            placeholder: 'Поиск по коду комплекта' }"
                           :onItemSelected="onSetSelected"
                           :onInputChange="onSetSearch">
                <div slot="item" slot-scope="props" class="single-item">
                  <div class="name"><b>{{ props.item.code }}</b></div>
                  <div class="name">{{ props.item.price | currency }}&nbsp;₽</div>
                  <div class="name">{{ props.item.section }}</div>
                </div>
              </suggestions>
            </b-form-group>
          </div>

          <div class="col-4">
            <b-form-group label="Наименование">
              <b-input v-model="set.section" :disabled="isSetNameDisabled()"></b-input>
            </b-form-group>
          </div>

          <div class="col-1">
            <b-form-group label="Кол-во">
              <b-input v-model="set.quantity" type="number" min="1"></b-input>
            </b-form-group>
          </div>

          <div class="col-2">
            <b-form-group label="Цена/шт">
              <b-input v-model.lazy="set.price" v-money="moneyConfig" ref="price"></b-input>
            </b-form-group>
          </div>

          <div class="col">
            <b-form-group>
              <label>&nbsp;</label>
              <b-btn block variant="outline-primary" @click="addPositionToInvoice" :disabled="isAddSetBtnDisabled()">
                <font-awesome-icon icon="plus"/>
              </b-btn>
            </b-form-group>
          </div>
        </b-form-row>

        <set-list :sets.sync="innerInvoice.sets" :custom-items.sync="innerInvoice.custom_items"></set-list>
      </div>

      <div class="block">

        <h3>Прочее</h3>
        <b-tabs content-class="mt-3">
          <b-tab title="Условия">
            <b-form-group label="Срок поставки, календарные дни" :invalid-feedback="fieldError('days')"
                          :state="fieldState('days')">
              <b-input v-model="innerInvoice.days" :state="fieldState('days')"></b-input>
            </b-form-group>
            <b-form-group label="Предоплата" :invalid-feedback="fieldError('prepay')"
                          :state="fieldState('prepay')">
              <b-input v-model="innerInvoice.prepay" :state="fieldState('prepay')"></b-input>
            </b-form-group>
            <b-form-group label="Доставка" :invalid-feedback="fieldError('delivery')"
                          :state="fieldState('delivery')">
              <b-input v-model.trim="delivery" :state="fieldState('delivery')"></b-input>
            </b-form-group>

            <b-form-group>
              <b-check v-model="innerInvoice.install" :state="fieldState('install')">
                Установка
                <b-form-invalid-feedback :state="fieldState('install')">
                  {{ fieldError('install') }}
                </b-form-invalid-feedback>
              </b-check>
            </b-form-group>
          </b-tab>

          <b-tab title="Входящие/Исходящие">
            <b-form-row>
              <div class="col">
                <b-form-group label="Входящий Галсен"
                              :invalid-feedback="fieldError('reference_numbers.galsen.inbound')"
                              :state="fieldState('reference_numbers.galsen.inbound')">
                  <b-input-group>
                    <b-input :state="fieldState('reference_numbers.galsen.inbound')"
                             v-model="innerInvoice.reference_numbers.galsen.inbound" placeholder="№"></b-input>
                    <masked-input mask="11.11.1111" type="text"
                                  v-model="innerInvoice.reference_numbers.galsen.inbound_date"
                                  class="form-control" placeholder="Дата"></masked-input>
                  </b-input-group>
                </b-form-group>
              </div>
              <div class="col">
                <b-form-group label="Электросфера"
                              :invalid-feedback="fieldError('reference_numbers.electrosphere.inbound')"
                              :state="fieldState('reference_numbers.electrosphere.inbound')">
                  <b-input-group>
                    <b-input :state="fieldState('reference_numbers.electrosphere.inbound')"
                             v-model="innerInvoice.reference_numbers.electrosphere.inbound"
                             placeholder="№"></b-input>
                    <masked-input mask="11.11.1111" type="text"
                                  v-model="innerInvoice.reference_numbers.electrosphere.inbound_date"
                                  class="form-control" placeholder="Дата"></masked-input>
                  </b-input-group>
                </b-form-group>
              </div>
              <div class="col">
                <b-form-group label="Афанасьев"
                              :invalid-feedback="fieldError('reference_numbers.afanasev.inbound')"
                              :state="fieldState('reference_numbers.afanasev.inbound')">
                  <b-input-group>
                    <b-input :state="fieldState('reference_numbers.afanasev.inbound')"
                             v-model="innerInvoice.reference_numbers.afanasev.inbound" placeholder="№"></b-input>
                    <masked-input mask="11.11.1111" type="text"
                                  v-model="innerInvoice.reference_numbers.afanasev.inbound_date"
                                  class="form-control" placeholder="Дата"></masked-input>
                  </b-input-group>
                </b-form-group>
              </div>
            </b-form-row>

            <b-form-row>
              <div class="col">
                <b-form-group label="Исходящий Галсен"
                              :invalid-feedback="fieldError('reference_numbers.galsen.outbound')"
                              :state="fieldState('reference_numbers.galsen.outbound')">
                  <b-input-group>
                    <b-input :state="fieldState('reference_numbers.galsen.outbound')"
                             v-model="innerInvoice.reference_numbers.galsen.outbound" placeholder="№"></b-input>
                    <masked-input mask="11.11.1111" type="text"
                                  v-model="innerInvoice.reference_numbers.galsen.outbound_date"
                                  class="form-control" placeholder="Дата"></masked-input>
                  </b-input-group>
                </b-form-group>
              </div>
              <div class="col">
                <b-form-group label="Электросфера"
                              :invalid-feedback="fieldError('reference_numbers.electrosphere.outbound')"
                              :state="fieldState('reference_numbers.electrosphere.outbound')">
                  <b-input-group>
                    <b-input :state="fieldState('reference_numbers.electrosphere.outbound')"
                             v-model="innerInvoice.reference_numbers.electrosphere.outbound"
                             placeholder="№"></b-input>
                    <masked-input mask="11.11.1111" type="text"
                                  v-model="innerInvoice.reference_numbers.electrosphere.outbound_date"
                                  class="form-control" placeholder="Дата"></masked-input>
                  </b-input-group>
                </b-form-group>
              </div>
              <div class="col">
                <b-form-group label="Афанасьев"
                              :invalid-feedback="fieldError('reference_numbers.afanasev.outbound')"
                              :state="fieldState('reference_numbers.afanasev.outbound')">
                  <b-input-group>
                    <b-input :state="fieldState('reference_numbers.afanasev.outbound')"
                             v-model="innerInvoice.reference_numbers.afanasev.outbound"
                             placeholder="№"></b-input>
                    <masked-input mask="11.11.1111" type="text"
                                  v-model="innerInvoice.reference_numbers.afanasev.outbound_date"
                                  class="form-control" placeholder="Дата"></masked-input>
                  </b-input-group>
                </b-form-group>
              </div>
            </b-form-row>
          </b-tab>

          <b-tab title="Комментарии">
            <comments :comments="innerInvoice.comments"></comments>

            <b-form-group label="Добавить комментарий" :invalid-feedback="fieldError('comment')"
                          :state="fieldState('comment')">
              <b-textarea v-model="innerInvoice.comment" :state="fieldState('comment')" :trim="true"
                          max-rows="20"></b-textarea>
            </b-form-group>
          </b-tab>
        </b-tabs>

        <div class="text-right mt-3">
          <b-btn :disabled="! invoice.can.delete" @click="onDelete()" v-if="innerInvoice.id"
                 variant="outline-light" class="text-danger mr-3">Удалить</b-btn>
          <b-btn-group>
            <b-btn :disabled="innerInvoice.id ? ! invoice.can.update : ! $page.props.can.invoices['create']"
                @click="onSubmit($event)" size="lg" variant="outline-primary">Применить</b-btn>
            <b-btn :disabled="innerInvoice.id ? ! invoice.can.update : ! $page.props.can.invoices['create']"
                @click="onSubmit($event, true)" size="lg" variant="outline-primary">Сохранить и закрыть</b-btn>
          </b-btn-group>
        </div>
      </div>
    </b-form>
  </b-card>
</template>

<script>
import 'v-suggestions/dist/v-suggestions.css'
import MaskedInput from 'vue-masked-input'
import Suggestions from 'v-suggestions'
import SetList from '~/Components/Set/SetList'
import MoneyConfig from '~/Components/MoneyConfig'
import Comments from '~/Components/Comment/Comments'

export default {
  components: {
    Comments,
    MaskedInput,
    Suggestions,
    SetList
  },
  props: {
    invoice: Object,
    sets: Array,
    companies: Array
  },
  data () {
    return {
      innerInvoice: {
        id: null,
        status: 1,
        company: {
          id: null,
          name: '',
          city: null
        },
        person: {
          id: null,
          name: '',
          position: null,
          phone: null,
          email: null
        },
        reference_numbers: {
          galsen: {
            inbound: 'б/н',
            inbound_date: '',
            outbound: '',
            outbound_date: new Date().toJSON().slice(0, 10).split('-').reverse().join('.').toString()
          },
          electrosphere: {
            inbound: 'б/н',
            inbound_date: '',
            outbound: '',
            outbound_date: new Date().toJSON().slice(0, 10).split('-').reverse().join('.').toString()
          },
          afanasev: {
            inbound: 'б/н',
            inbound_date: '',
            outbound: '',
            outbound_date: new Date().toJSON().slice(0, 10).split('-').reverse().join('.').toString()
          }
        },
        install: false,
        days: '50',
        prepay: 'Предоплата 30%, окончательный расчет по факту поставки',
        delivery: null,
        sets: [],
        custom_items: [],
        comments: [],
        comment: null
      },
      moneyConfig: MoneyConfig,
      setQuery: '',
      set: {
        id: null,
        name: null,
        code: null,
        price: 0,
        quantity: 1
      }
    }
  },
  created () {
    if (this.invoice) {
      this.innerInvoice = this.invoice
    }
  },
  beforeUpdate () {
    // workaround for v-suggestions
    this.$refs.companyNameEl.$el.children[0].classList.remove('is-invalid')

    if (this.fieldError('company.name')) {
      this.$refs.companyNameEl.$el.children[0].classList.add('is-invalid')
    }
  },

  computed: {
    delivery: {
      get () {
        const currentDelivery = this.innerInvoice.delivery
        const defaultDelivery = `транспортные расходы (с учетом доставки до Заказчика в г. ${this.innerInvoice.company.city || ''}, погрузки-разгрузки, подъема на этаж`

        if (!currentDelivery) {
          return defaultDelivery
        }

        const getDiff = (string, diffBy) => string.split(diffBy).join(' ')
        const diff = getDiff(currentDelivery, defaultDelivery)

        if (diff === this.innerInvoice.company.city) {
          return defaultDelivery
        } else {
          return currentDelivery
        }
      },

      set (newValue) {
        this.innerInvoice.delivery = newValue
      }
    }
  },
  methods: {
    onSubmit (event, redirectToList = false) {
      if (!this.innerInvoice.delivery) {
        this.innerInvoice.delivery = this.delivery
      }

      if (Number.isInteger(this.innerInvoice.id)) {
        const route = this.route('backend.proforma-invoices.update', {
          proforma_invoice: this.innerInvoice.id,
          ...(redirectToList && { redirectToList: true })
        })

        this.$inertia.put(route, this.innerInvoice)
        return
      }

      const route = this.route('backend.proforma-invoices.store', { ...(redirectToList && { redirectToList: true }) })
      this.$inertia.post(route, this.innerInvoice)
    },

    onDelete () {
      if (!confirm('Вы уверены?')) {
        return
      }

      this.$inertia.delete(this.route('backend.proforma-invoices.destroy', this.innerInvoice.id))
    },

    isCompanyEditingDisabled (field) {
      const company = this.companies.find(company => this.innerInvoice.company.id === company.id)

      return typeof company === 'object' && !(company[field] === '' || company[field] === null)
    },

    isSetNameDisabled () {
      return this.setQuery === this.set.code
    },

    isAddSetBtnDisabled () {
      return (!this.set.code && this.setQuery === '') ||
          this.set.quantity === '' ||
          this.set.price === ''
    },

    /**
     * Обработчик выбора в списке подсказок автокомплита компаний
     */
    onCompanySelected (item) {
      this.innerInvoice.company = { ...item }
      this.innerInvoice.person = { ...item.person }
    },

    /**
     * Обработчик инпута автокомплита компаний
     */
    onCompanySearch (companyQuery) {
      if (companyQuery.trim().length === 0) {
        this.innerInvoice.company = {
          id: null,
          name: null,
          city: null
        }
        return
      }

      const result = []

      this.companies.forEach((company) => {
        if (
          (typeof company.name === 'string' && company.name.toLowerCase().includes(companyQuery.toLowerCase())) ||
            (typeof company.city === 'string' && company.city.toLowerCase().includes(companyQuery.toLowerCase()))
        ) {
          result.push(company)
        }
      })

      return result
    },

    /**
     * Обработчик выбора в списке подсказок автокомплита комплектов
     */
    onSetSelected (item) {
      this.setQuery = item.code
      this.$refs.price.$el.value = item.price // workaround для v-money v-bootstrap
      this.set = { ...item }
      this.set.quantity = this.set.quantity || 1
    },

    /**
     * Обработчик инпута автокомплита комплектов
     */
    onSetSearch (setQuery) {
      if (setQuery.trim().length === 0) {
        return null
      }

      const result = []

      this.sets.forEach((item) => {
        if (
          (typeof item.code === 'string' && item.code.toLowerCase().includes(setQuery.toLowerCase())) ||
            (typeof item.name === 'string' && item.name.toLowerCase().includes(setQuery.toLowerCase()))
        ) {
          result.push(item)
        }
      })

      return result
    },

    /**
     * Добавление позиции в состав инвойса
     */
    addPositionToInvoice () {
      if (!this.innerInvoice.sets) {
        this.innerInvoice.sets = []
      }

      if (!this.innerInvoice.custom_items) {
        this.innerInvoice.custom_items = []
      }

      if (!this.set.code || this.set.code !== this.setQuery) {
        // Добавление кастома
        this.set.id = null
        this.set.code = this.setQuery
        this.innerInvoice.custom_items.push(this.set)
      } else {
        // Добавление стандартного комплекта
        this.innerInvoice.sets.push(this.set)
      }

      // Очищаем форму и прочее
      this.setQuery = ''

      this.$nextTick(() => {
        this.set = {
          id: null,
          name: null,
          code: null,
          price: 0,
          quantity: 1
        }
      })
    }
  }
}
</script>

<style>
.collapsed > .when-opened,
:not(.collapsed) > .when-closed {
  display: none;
}

.v-suggestions .suggestions {
  max-height: 200px;
  overflow-y: scroll;
}

.block {
  padding: 20px;
}
</style>
